import { Button, Tooltip, Typography } from "@mui/material";
import React from "react";
import { ModalContext } from "../../../../Components/Modals";
import { GridRowsProp, GridColDef, GridRowParams, GridActionsCellItem } from "@mui/x-data-grid";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import { jobBrokerService, userService } from "../../../../Core/Api/services";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { GroupAdvancedInfos } from "../../../../Core/Api/Types/group-advanced-infos";
import AddIcon from "@mui/icons-material/Add";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import EditIcon from "@mui/icons-material/Edit";
import ModalConfirmAction, {
    ModalConfirmActionElementProps,
} from "../../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { UserHasAcl } from "../../../../Utils/helpers";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import { DbJobRuleInfos } from "../../../../Core/Api/Types/db-job-rule-infos";
import { useNavigate } from "react-router";
import { routePaths } from "../../../../Router/router";
import TableCellChip from "../../../../Components/Table/TableCellChip/table-cell-chip";
import TableCellBool from "../../../../Components/Table/TableCellBool";
import { useTranslation } from "react-i18next";
import { DeleteOutlineOutlined } from "@mui/icons-material";

function AdminSettingsJobRulesManagement() {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = React.useState(true);
    const [rows, setRows] = React.useState<DbJobRuleInfos[]>([]);
    const [groups, setGroups] = React.useState<GroupAdvancedInfos[]>([]);
    const [trigger, setTrigger] = React.useState<boolean>(false);
    const enqueueCustomSnackbar = useCustomSnackbar();
    const navigate = useNavigate();

    // Table rows
    const apiGrid = useGridApiRef();

    // Modal
    const modalContext = React.useContext(ModalContext);
    const { openModal } = { ...modalContext };

    React.useEffect(() => {
        Promise.all([jobBrokerService.ListRules(), userService.GetGroupsList()]).then(
            ([jobRulesResponse, groupsResonse]) => {
                setRows(jobRulesResponse.rules || []);
                setGroups(groupsResonse.groups || []);
                setIsLoading(false);
            }
        );
    }, [trigger]);

    // #region Grid definition
    let _rows: GridRowsProp = [];
    type Row = (typeof _rows)[number];

    const columns: GridColDef[] = [
        {
            field: "id",
            headerName: "Id",
            flex: CellSize.xsmall,
            minWidth: CellSize.small,

            renderCell: (data: Row) => (
                <Typography title={data.value} variant="body1">
                    {data.value || "-"}
                </Typography>
            ),
        },
        {
            field: "name",
            headerName: t("common:name"),
            flex: CellSize.xlarge,
            minWidth: CellSize.xlarge,
            renderCell: (data: Row) => (
                <Typography title={data.value} variant="body1">
                    {data.value || "-"}
                </Typography>
            ),
        },
        {
            field: "groupIds",
            headerName: t("table:headerName.userGroup"),
            flex: CellSize.large,
            minWidth: CellSize.large,
            renderCell: (data: Row) =>
                data.value && data.value.length > 0 ? (
                    <TableCellChip
                        data={{ value: data.value.map((x) => groups.find((g) => g.id === x)?.group?.name || "Unknown") ?? [] }}
                    />
                ) : (
                    <></>
                ),
        },
        {
            field: "platformIds",
            headerName: t("table:headerName.shared"),
            flex: CellSize.xsmall,
            minWidth: CellSize.xsmall,
            renderCell: (data: Row) => <TableCellBool value={data?.row?.platformIds.length > 1} />,
        },
        {
            field: "enabled",
            headerName: t("table:headerName.enabled"),
            flex: CellSize.xsmall,
            minWidth: CellSize.xsmall,
            renderCell: (data: Row) => <TableCellBool value={data.value} />,
        },
        {
            field: "inputFileCategory",
            headerName: t("table:headerName.inputFileCategory"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (data: Row) => <TableCellChip data={{ value: [data.value ?? "-"] }} />,
        },
        {
            field: "tag",
            headerName: t("common:tag"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (data: Row) => <TableCellChip data={{ value: [data.value ?? "-"] }} />,
        },
        {
            field: "actions",
            headerName: "",
            type: "actions",
            flex: CellSize.actionsLarge,
            minWidth: CellSize.actionsLarge,
            align: "right",
            getActions: (params: GridRowParams) => [
                <GridActionsCellItem
                    icon={
                        <Tooltip title={t("tooltip:title.copyJobRole")}>
                            <FileCopyIcon sx={{ width: 20, height: 20, color: "text.primary" }} />
                        </Tooltip>
                    }
                    label={t("common:copy")}
                    title={t("common:copy")}
                    disabled={!UserHasAcl(AclTypes.aclSuperUser)}
                    onClick={() => {
                        navigate(routePaths.createJobRule.path, { state: { ...params.row, id: -500 } });
                    }}
                />,
                <GridActionsCellItem
                    icon={
                        <Tooltip title={t("tooltip:title.editJobRule")}>
                            <EditIcon sx={{ width: 20, height: 20, color: "primary.text" }} />
                        </Tooltip>
                    }
                    label={t("common:modify")}
                    title={t("common:modify")}
                    disabled={!UserHasAcl(AclTypes.aclSuperUser)}
                    onClick={() => {
                        navigate(routePaths.editJobRule.path, { state: params.row });
                    }}
                />,
                <GridActionsCellItem
                    icon={
                        <Tooltip title={t("tooltip:title.deleteJobRule")}>
                            <DeleteOutlineOutlined sx={{ width: 20, height: 20, color: "error.main" }} />
                        </Tooltip>
                    }
                    label={t("common:delete")}
                    title={t("common:delete")}
                    disabled={!UserHasAcl(AclTypes.aclSuperUser)}
                    onClick={() => {
                        if (!openModal) return;

                        openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
                            displayComment: true,
                            commentLabel: t("modal:comment.confirmDeletePriceRule"),
                            title: t("modal:title.deletePriceRule"),
                            onConfirm: () => {
                                jobBrokerService.DeleteRule(params?.row?.id).then(() => {
                                    enqueueCustomSnackbar(t("alert:success.jobRuleDeleted"), "success");
                                    setTrigger((prevState) => !prevState);
                                });
                            },
                        });
                    }}
                />,
            ],
        },
    ];
    // #endregion

    const additionalActions = (
        <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
                navigate(routePaths.createJobRule.path, {
                    state: new DbJobRuleInfos({
                        description: "",
                        name: "",
                        enabled: true,
                        groupIds: [],
                        id: -500,
                        inputFileCategory: null,
                        platformIds: [],
                        tag: null,
                        postActions: [],
                        jobsDefinitions: [],
                        filter: {
                            objectClasses: [],
                            metadataMatch: [],
                            objectLayers: [],
                            objectStatuses: [],
                            objectTypes: [],
                            checkForStorageOnlineCopy: null,
                        },
                    }),
                });
            }}
        >
            {t("button:newJobRule")}
        </Button>
    );

    return (

        <DataTable
            loading={isLoading}
            columns={columns}
            rows={rows}
            additionalActions={additionalActions}
            rowHeight={64}
            pagination={true}
            disableRowSelectionOnClick
            apiRef={apiGrid}
            getRowHeight={() => "auto"}
            onRowClick={(params) => {
                navigate(routePaths.editJobRule.path, { state: params.row });
            }}
        />
    );
}

export default AdminSettingsJobRulesManagement;
